import { useNavigate } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Select, SelectContent, SelectItem, SelectTrigger } from "design-system/molecules/Select"
import { Eye, Plus, Sparkles, X } from "lucide-react"
import { useCommonLocalesQuery } from "modules/campaigns/hooks/useCommonLocalesQuery"
import { useCreateTranslatedTemplatesMutation } from "modules/campaigns/hooks/useCreateTranslatedTemplatesMutation"
import { useTranslateWhatsappTemplateMutation } from "modules/campaigns/hooks/useTranslateWhatsappTemplateMutation"
import { Loading } from "modules/shared/components/Loading"
import { useLanguageLabel, useLanguageOptions } from "modules/shared/hooks/useLanguageLabel"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CreateTranslatedTemplatesSchema,
  TranslateWhatsappTemplateResponseData,
  WhatsappTemplateResponseDataSchema,
} from "schema"
import z from "zod"
import { WhatsappTemplatePreview } from "../WhatsappTemplatePreview"
import { WhatsappTemplateTextarea } from "./WhatsappTemplateTextarea"

type WhatsappTemplateTranslateProps = z.infer<typeof WhatsappTemplateResponseDataSchema>

export const WhatsappTemplateTranslate = ({
  body,
  name,
  language,
  id,
  whatsappTemplateVariables,
  mediaFormat,
  mediaUrl,
  whatsappTemplateButtons,
}: WhatsappTemplateTranslateProps) => {
  const buttonTexts = (whatsappTemplateButtons ?? []).map((button) => button.text)
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const languageOptions = useLanguageOptions()
  const navigate = useNavigate()
  const [languages, setLanguages] = useState<string[]>([])
  const { isPending: isCommonLocalesPending } = useCommonLocalesQuery((commonLocales) => {
    setLanguages(commonLocales.map((e) => e.locale).filter((e) => e !== language))
  })
  const [translations, setTranslations] = useState<z.infer<
    typeof TranslateWhatsappTemplateResponseData.shape.translations
  > | null>(null)
  const { mutateAsync, isPending: isTranslateTemplatesMutationPending } =
    useTranslateWhatsappTemplateMutation()

  const { mutateAsync: createTemplate, isPending: isCreateTemplatesPending } =
    useCreateTranslatedTemplatesMutation()

  const hasValidTranslations =
    CreateTranslatedTemplatesSchema.shape.translations.safeParse(translations).success

  const onSubmitLanguages = async () => {
    setTranslations(null)
    const response = await mutateAsync({ languages, id })

    setTranslations(response.translations)
  }

  const onToggleLanguage = (lang: string) => {
    const exists = languages.find((e) => e === lang)

    if (exists) {
      setLanguages((languages) => languages.filter((e) => e !== lang))
    } else {
      setLanguages((languages) => [...languages, lang])
    }
  }

  const onCreateTemplates = async () => {
    if (translations && hasValidTranslations) {
      await createTemplate({ id, translations, name, mediaFormat, mediaUrl })

      navigate({
        to: "/settings/channels/whatsapp/templates",
        search: { field: "name", order: "asc", page: 1, query: name },
      })
    }
  }

  if (isCommonLocalesPending) {
    return null
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant={"gradient"}>
          <Sparkles />
          <span>{t("campaigns.templates.whatsapp.detail.translateTemplate.buttonText")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-[700px] gap-4">
        <DialogHeader>
          <DialogTitle>
            {t("campaigns.templates.whatsapp.detail.translateTemplate.modal.title")}
          </DialogTitle>
          <DialogDescription>
            {t("campaigns.templates.whatsapp.detail.translateTemplate.modal.description")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <WhatsappTemplatePreview
            body={body}
            variables={whatsappTemplateVariables}
            buttonTexts={buttonTexts}
          />
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <Label>
                {t("campaigns.templates.whatsapp.detail.translateTemplate.modal.selectLanguages")}
              </Label>
              <span className="text-sm text-muted-foreground">
                {t("campaigns.templates.whatsapp.detail.translateTemplate.modal.commonLanguages")}
              </span>
            </div>
            <div className="flex align-bottom gap-3 justify-between">
              <div className="flex gap-2 items-center flex-wrap">
                {languages.map((language) => (
                  <div key={language}>
                    <Button
                      variant={"outline"}
                      className="flex pr-3"
                      onClick={() => onToggleLanguage(language)}
                    >
                      <span className="text-xs">{languageLabel(language)}</span>
                      <X className="h-3!" size={10} />
                    </Button>
                  </div>
                ))}
                <Select value="null" onValueChange={(language) => onToggleLanguage(language)}>
                  <SelectTrigger className="w-fit text-xs font-medium h-9">
                    <Plus size={12} />
                    <span>{t("shared.language")}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {languageOptions
                      .filter((e) => !languages.includes(e.value) && e.value !== language)
                      .map((language) => (
                        <SelectItem value={language.value} key={language.value}>
                          {language.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Button
                  variant={"gradient"}
                  onClick={onSubmitLanguages}
                  disabled={isTranslateTemplatesMutationPending || !languages.length}
                >
                  {isTranslateTemplatesMutationPending ? <Loading minimal /> : <Sparkles />}

                  <span>
                    {t(
                      "campaigns.templates.whatsapp.detail.translateTemplate.modal.generateTranslations",
                    )}
                  </span>
                </Button>
              </div>
            </div>
            {translations && (
              <div className="mt-4 flex flex-col gap-2">
                <DialogTitle>
                  {t(
                    "campaigns.templates.whatsapp.detail.translateTemplate.modal.suggestedTranslations",
                  )}
                </DialogTitle>
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-4">
                    {translations.map((translation) => (
                      <div key={translation.language} className="flex flex-col gap-1">
                        <div className="flex justify-between">
                          <span className="text-sm font-medium">
                            {languageLabel(translation.language)}
                          </span>
                          <Tooltip>
                            <TooltipTrigger className="flex gap-2 cursor-pointer">
                              <Eye size={16} />
                            </TooltipTrigger>
                            <TooltipContent className="p-0 text-inherit bg-transparent">
                              <WhatsappTemplatePreview
                                body={translation.body}
                                buttonTexts={buttonTexts.map(
                                  (text, index) => translation.buttonTexts?.[index] ?? text,
                                )}
                                variables={whatsappTemplateVariables}
                                className="rounded-none"
                              />
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <WhatsappTemplateTextarea
                          value={translation.body}
                          onChange={(value) => {
                            setTranslations(
                              (translations) =>
                                translations?.map((t) => ({
                                  ...t,
                                  body: t.language === translation.language ? value : t.body,
                                })) ?? [],
                            )
                          }}
                        />
                        {buttonTexts.map((buttonText, buttonIndex) => (
                          <div key={buttonIndex} className="mt-2 flex flex-col gap-2">
                            <Label htmlFor={`button-copy-${translation.language}-${buttonIndex}`}>
                              {t(
                                "campaigns.templates.whatsapp.detail.config.blocks.openLinkButtonText",
                              )}
                            </Label>
                            <Input
                              id={`button-copy-${translation.language}-${buttonIndex}`}
                              value={translation.buttonTexts?.[buttonIndex] ?? buttonText}
                              onChange={(event) => {
                                const value = event.target.value

                                setTranslations(
                                  (translations) =>
                                    translations?.map((t) =>
                                      t.language === translation.language
                                        ? {
                                            ...t,
                                            buttonTexts: buttonTexts.map((text, index) =>
                                              index === buttonIndex
                                                ? value
                                                : (t.buttonTexts?.[index] ?? text),
                                            ),
                                          }
                                        : t,
                                    ) ?? [],
                                )
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <Button
                      disabled={isCreateTemplatesPending || !hasValidTranslations}
                      onClick={onCreateTemplates}
                      className="flex gap-2"
                    >
                      {isCreateTemplatesPending && <Loading minimal />}
                      {t(
                        "campaigns.templates.whatsapp.detail.translateTemplate.modal.createTemplates",
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
